<template>
  <div class="farm-window employee-card" :style="cssVars">
    <img class="employee-thumb" :src="employee.image"/>
    <div class="info-panel">

      <div style="display: grid; grid-template-columns: 1fr 1fr;">
        <h2>
          {{ employee.name }}
          <span style="font-size: 0.75em; font-weight: normal;">
          (<span v-for="(p, i) in employee.personalities" :title="p.description()">
          {{ util.capitalize(p.id) }}<span v-if="i != employee.personalities.length - 1">&nbsp;</span>
          </span>)
        </span>
        </h2>
        <h3 style="text-align: right; margin: 0; line-height: 1.5;" title="The job this employee is assigned to.">{{ employee.assignmentTitle() }}</h3>
      </div>

      <div class="stat-bars">
        <p :title="`Per day salary and other costs for this employee.`">Costs</p>
        <div class="option-buttons" style="grid-template-columns: 2fr 1fr 1fr">
          <p>${{ (employee.salary.value * 60).toFixed(0) }} salary, <span>${{(employee.inventory.ongoingCost * 60).toFixed(0) }} upkeep</span> </p>
          <TextButton class="farm-button" @click="employee.salary.baseValue -= 0.05"> <p>-</p> </TextButton>
          <TextButton class="farm-button" @click="employee.salary.baseValue += 0.05"> <p>+</p> </TextButton>
        </div>

        <!--<p :title="`Tell ${employee.name} which tasks she should be doing.`">Job</p>
        <div class="option-buttons">
          <TextButton @click="farm.assignWork(employee, 'office')"
                      class="farm-button"
                      :class="{'selected-button': employee.assignment === 'office'}"
                      :title="employee.name + ' will help you run the farm and increase milk price.'">
            <p>Office</p>
          </TextButton>

          <TextButton @click="farm.assignWork(employee, 'farm')"
                      class="farm-button"
                      :class="{'selected-button': employee.assignment === 'farm'}"
                      :title="employee.name + ' will focus on eating and sleeping well to produce more milk.'">
            <p>Pasture</p>
          </TextButton>
          <TextButton @click="farm.assignWork(employee, 'lab')"
                      class="farm-button"
                      :class="{'selected-button': employee.assignment === 'lab'}"
                      :title="employee.name + ' will use their intelligence to unlock new research quicker.'">
            <p>Lab</p>
          </TextButton>
        </div>-->

        <p :title="`More smarts means more science, if assigned to the lab.`">IQ</p>
        <ValueMeter :container="employee.iq" barColor="#149">
          <p>{{ employee.iq.value.toFixed(0) }}, {{ employee.iq.text }}</p>
        </ValueMeter>

        <p :title="`You want ${employee.name} to be happy, right?`">Mood</p>
        <div class="option-buttons" style="grid-template-columns: 2fr 1fr">
          <ValueMeter :container="employee.mood" barColor="#194">
            {{ employee.mood.value.toFixed(0)}} /
            {{ employee.mood.max.toFixed(0) }},
            {{ employee.mood.text }}
          </ValueMeter>
          <TextButton class="farm-button"
                      @click="farm.gift(employee)"
                      :title="`Everyone likes a gift!`"
          >
            <p><b>Gift</b> (${{ Math.round(farm.giftPrice) }})</p>
          </TextButton>
        </div>

        <p :title="`Being horny usually makes concentration difficult, but producing milk easier.`">Lust</p>
        <div class="option-buttons" style="grid-template-columns: 4fr 1fr 1fr">
          <ValueMeter :container="employee.lust"
                      barColor="#c58">
            {{ employee.lust.value.toFixed(0)}} /
            {{ employee.lust.max.toFixed(0) }},
            {{ employee.lust.text }}
          </ValueMeter>
          <TextButton class="farm-button"
                      @click="farm.cuddle(employee)"
                      :title="`Snuggle up under a blanket together, light some candles and listen to the rain outside.`"
                      :class="{'disabled-button': !employee.canCuddle}"
          >
            <p><b>Cuddle</b></p>
          </TextButton>
          <TextButton class="farm-button"
                      @click="farm.fuck(employee)"
                      :title="`What cowgirl says no to some hot, steamy sex?`"
                      :class="{'disabled-button': !employee.canFuck}"
          >
            <p><b>Fuck</b></p>
          </TextButton>
        </div>

        <p title="Nature's white gold. This is what we're here for.">Milk</p>
        <div class="option-buttons" style="grid-template-columns: 2fr 1fr">
          <ValueMeter :container="employee.milk"
                      barColor="#abc"
                      :title="`Max storage: ${employee.milk.max.toFixed(1)} L`">
            {{ util.showNumber(employee.milk.value, employee.milkDelta.value * 60) }} /
            {{ util.showNumber(employee.milk.max, employee.milkDelta.value * 60) }} L
            ({{ util.showNumber(employee.milkDelta.value * 60) }} L/day)
          </ValueMeter>
          <TextButton class="farm-button"
                      @mousedown="farm.startMilking(employee)"
                      @touchstart="farm.startMilking(employee)"
                      @mouseup="farm.stopMilking(employee)"
                      @mouseleave="farm.stopMilking(employee)"
                      @touchend="farm.stopMilking(employee)"
          >
            <p><b>Milk</b> <i>({{ util.showNumber(farm.state.milkingSpeed.value * 60) }} L/day)</i></p>
          </TextButton>
        </div>

        <p>Chat</p>
        <div class="farm-list chat">
          <div v-for="msg in employee.chatHistory.history">
            <p>[{{ util.currentDate(msg.time) }}] {{ msg.msg }}</p>
          </div>
        </div>
      </div>

      <div class="equipment-list">
        <div v-for="item in employee.inventory.allItems">
          <ResearchThumb :farm="farm" :item="item"></ResearchThumb>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import Employee from '@/employee/Employee'
import Farm from '@/farm/Farm'
import ValueMeter from '@/components/ui/ValueMeter.vue'
import TextButton from '@/components/ui/TextButton.vue'
import util from '@/util'
import ResearchThumb from "@/components/ResearchThumb.vue"

@Options({
  components: {
    ValueMeter, TextButton, ResearchThumb
  },
  props: {
    employee: Employee, farm: Farm
  },
  methods: {
    log(msg: any) {
      console.log(msg)
    },
    deltaSymbol(value: number): string {
      if (value > 0) return 'Increasing'
      else if (value < 0) return 'Decreasing'
      return 'Stable'
    }
  },
  computed: {
    cssVars() {
      return {
        '--background-image': 'url(' + require('@/assets/wolfgang-hasselmann.jpg') + ')'
      }
    },
    util() {
      return util
    }
  }
})
export default class EmployeeCard extends Vue {
  employee!: Employee
  farm!: Farm
}
</script>

<style scoped lang="scss">
  .employee-card {
    display: grid;
    grid-template-columns: 3fr 4fr;
    grid-template-rows: 1fr;
    grid-template-areas: "a b";
    height: 100%;
    max-height: 500px;
    max-width: $main-column-max-width;
    margin-bottom: 0;
    margin-left: auto;
    margin-right: auto;
    padding: 0;

    @media only screen and (max-width: $mobile-width) {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
      grid-template-areas:  "a"
                            "b";

      max-height: none;
    }
  }

  .employee-thumb {
    height: 100%;
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    object-position: 50% 25%;

    background-color: $component-background-color;
    //background-image: var(--background-image);
    background-attachment: local;
    background-size: cover;
    background-position: center;
    grid-area: a;

    @media only screen and (max-width: $mobile-width) {
      height: 300px;
    }
  }

  .info-panel {
    grid-area: b;
    padding: 0.5em;
    text-align: left;
    border-left: inherit;

    h3 {
      margin-top: 0;
      margin-bottom: 0.5em;
    }
  }

  .stat-bars {
    display: grid;
    grid-template-columns: 50px 1fr;
    grid-auto-rows: 40px;
    grid-gap: 5px;
    margin-bottom: 1em;

    .value-meter {
      margin-top: auto;
      margin-bottom: auto;
      height: 95%;
    }

    p {
      margin-top: auto;
      margin-bottom: auto;
    }

    & > p {
      text-align: right;
      margin-right: 5px;
      font-weight: bold;
    }
  }

  .option-buttons {
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;

    p {
      text-align: center;
    }

    .text-button {
      display: table;
      margin-top: auto;
      margin-bottom: auto;
      height: 95%;
      border: $component-border-style;
      border-right: 0;

      &:last-child {
        border: $component-border-style;
      }

      p {
        display: table-cell;
        vertical-align: middle;
      }
    }

    .selected-button {
      background-color: scale-color(darken($highlight-color, 10%), $saturation: -10%);
      //border-color: $highlight-color;
      p {
        //color: $highlight-color;
      }
    }

    .value-meter {
      border-right-width: 0;
    }
  }

  .equipment-list {
    display: grid;
    grid-gap: 5px;
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  }

  .chat {
    grid-row: span 2;
    padding: 5px;

    p {
      text-align: left;
    }
  }

</style>
